import React, { useState, memo } from 'react';
import { Link } from 'react-router-dom';
import { HeroSection } from '../../components/HeroSection';
import { ArrowRight, ExternalLink, Mail } from 'lucide-react';
import { motion } from 'framer-motion';
interface Contributor {
  name: string;
  role: string;
  bio: string;
  photo: string; // Tailwind bg class as placeholder
  contributions: {
    title: string;
    type: string;
    slug: string;
  }[];
  social?: {
    website?: string;
    email?: string;
    instagram?: string;
    twitter?: string;
  };
}
const contributors: Contributor[] = [{
  name: 'Jordan Miller',
  role: 'Writer & Urban Planner',
  bio: 'Jordan is a fat writer and urban planner based in Chicago. Their work examines how the built environment shapes who gets to move through public space with dignity and ease. They hold an MFA in Creative Nonfiction from Columbia University.',
  photo: 'bg-brand-magenta',
  contributions: [{
    title: 'The Architecture of Exclusion',
    type: 'Essay',
    slug: 'architecture-of-exclusion'
  }],
  social: {
    website: 'https://example.com',
    instagram: 'jordanmiller',
    twitter: 'jordanmiller'
  }
}, {
  name: 'Sam Rivera',
  role: 'Poet & Essayist',
  bio: 'Sam is a fat queer poet whose work explores appetite, desire, and embodiment. Their poetry has appeared in numerous journals and anthologies. They facilitate creative writing workshops centred on fat joy and liberation.',
  photo: 'bg-brand-cyan',
  contributions: [{
    title: 'Hunger as Resistance',
    type: 'Poetry',
    slug: 'hunger-as-resistance'
  }, {
    title: 'Body as Archive',
    type: 'Poetry',
    slug: 'body-as-archive'
  }, {
    title: 'Fat Grief: Mourning the Body You Were Told to Hate',
    type: 'Essay',
    slug: 'fat-grief'
  }],
  social: {
    instagram: 'samrivera',
    email: 'sam@example.com'
  }
}, {
  name: 'Taylor Kim',
  role: 'Investigative Reporter',
  bio: 'Taylor is an investigative journalist covering the intersections of healthcare, industry, and fat oppression. Their reporting follows the money behind weight-loss interventions and holds institutions accountable.',
  photo: 'bg-brand-lime',
  contributions: [{
    title: 'Inside the Bariatric Surgery Industry',
    type: 'Investigation',
    slug: 'bariatric-surgery-industry'
  }],
  social: {
    twitter: 'taylorkim',
    website: 'https://example.com'
  }
}, {
  name: 'Alex Chen',
  role: 'Cultural Critic',
  bio: 'Alex writes about media representation, pop culture, and the politics of visibility. They are interested in how mainstream inclusion can coexist with — and sometimes obscure — structural oppression.',
  photo: 'bg-brand-orange',
  contributions: [{
    title: "Beyond Representation: Why Seeing Fat People on TV Isn't Enough",
    type: 'Essay',
    slug: 'beyond-representation'
  }],
  social: {
    twitter: 'alexchen'
  }
}, {
  name: 'Riley Martinez',
  role: 'Photographer & Visual Artist',
  bio: 'Riley is a fat Latinx photographer whose work centres fat joy, community, and radical imagination. Their visual essays challenge the ways fat bodies are typically represented in media.',
  photo: 'bg-brand-navy',
  contributions: [{
    title: 'Fat Futures: A Visual Essay',
    type: 'Visual Essay',
    slug: 'fat-joy-photo-series'
  }],
  social: {
    instagram: 'rileymartinez',
    website: 'https://example.com'
  }
}, {
  name: 'Morgan Lee',
  role: 'Education Reporter',
  bio: 'Morgan covers the intersection of fatphobia and education systems, from BMI surveillance in schools to diet culture in curricula. They are committed to exposing how institutions harm fat young people.',
  photo: 'bg-brand-magenta/70',
  contributions: [{
    title: 'How Schools Weaponize BMI',
    type: 'Investigation',
    slug: 'schools-weaponize-bmi'
  }, {
    title: 'Diet Culture in the Classroom',
    type: 'Reporting',
    slug: 'diet-culture-in-schools'
  }],
  social: {
    email: 'morgan@example.com',
    twitter: 'morganlee'
  }
}, {
  name: 'Priya Desai',
  role: 'Fashion & Culture Writer',
  bio: 'Priya writes about fat fashion, the plus-size industrial complex, and the creative ecosystems fat people are building outside the mainstream. They bring a sharp political lens to style and self-expression.',
  photo: 'bg-brand-cyan/70',
  contributions: [{
    title: 'Fat Fashion Reclaimed',
    type: 'Essay',
    slug: 'fat-fashion-reclaimed'
  }],
  social: {
    instagram: 'priyadesai'
  }
}, {
  name: 'Casey Johnson',
  role: 'Poet',
  bio: 'Casey is a poet exploring memory, inheritance, and what our bodies hold across generations. Their work sits at the intersection of fat embodiment and intergenerational trauma.',
  photo: 'bg-brand-lime/70',
  contributions: [{
    title: 'Body as Archive',
    type: 'Poetry',
    slug: 'body-as-archive'
  }],
  social: {
    email: 'casey@example.com'
  }
}];
export function Contributors() {
  const [selectedContributor, setSelectedContributor] = useState<number | null>(null);
  return <div className="min-h-screen bg-brand-cream" data-id="element-1053">
      <HeroSection title="Our Contributors" subtitle="The writers, poets, artists, and reporters who make WAESTM possible." tag="The Publication" bgColor="orange" data-id="element-1054" />

      {/* Introduction */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto text-center" data-id="element-1055">
        <p className="text-xl text-gray-700 leading-relaxed" data-id="element-1056">
          WAESTM is built by{' '}
          <strong className="text-brand-orange" data-id="element-1057">fat creators</strong> — writers,
          poets, photographers, illustrators, and reporters who bring lived
          experience, intellectual rigour, and creative courage to every piece.
          Here are the people behind the work.
        </p>
      </section>

      {/* Contributors Grid */}
      <section className="py-8 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto" data-id="element-1058">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-1059">
          {contributors.map((contributor, index) => <motion.div key={contributor.name} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: index * 0.06
        }} className="bg-white border-2 border-brand-black overflow-hidden" data-id="element-1060">
              <div className="flex flex-col sm:flex-row" data-id="element-1061">
                {/* Photo */}
                <div className={`w-full sm:w-40 h-48 sm:h-auto ${contributor.photo} flex-shrink-0 relative`} data-id="element-1062">
                  <div className="absolute inset-0 bg-white/10" data-id="element-1063" />
                </div>

                {/* Info */}
                <div className="p-6 flex-grow flex flex-col" data-id="element-1064">
                  <div className="mb-4" data-id="element-1065">
                    <h3 className="text-xl font-black text-brand-black mb-1" data-id="element-1066">
                      {contributor.name}
                    </h3>
                    <p className="text-sm font-bold uppercase tracking-wider text-brand-orange" data-id="element-1067">
                      {contributor.role}
                    </p>
                  </div>

                  <p className="text-sm text-gray-700 leading-relaxed mb-4 flex-grow" data-id="element-1068">
                    {contributor.bio}
                  </p>

                  {/* Contributions */}
                  <div className="mb-4" data-id="element-1069">
                    <p className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2" data-id="element-1070">
                      Contributions
                    </p>
                    <div className="space-y-1" data-id="element-1071">
                      {contributor.contributions.map(item => <Link key={item.slug} to={`/blog/${item.slug}`} className="flex items-center gap-2 text-sm text-brand-black hover:text-brand-orange transition-colors group" data-id="element-1072">
                          <ArrowRight className="h-3 w-3 text-brand-orange opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" data-id="element-1073" />
                          <span className="font-medium" data-id="element-1074">{item.title}</span>
                          <span className="text-xs text-gray-400" data-id="element-1075">
                            ({item.type})
                          </span>
                        </Link>)}
                    </div>
                  </div>

                  {/* Social Links */}
                  {contributor.social && <div className="flex items-center gap-3 pt-3 border-t border-gray-100" data-id="element-1076">
                      {contributor.social.website && <a href={contributor.social.website} target="_blank" rel="noopener noreferrer" className="text-xs font-bold uppercase tracking-wider text-gray-500 hover:text-brand-orange transition-colors flex items-center gap-1" data-id="element-1077">
                          <ExternalLink className="h-3 w-3" data-id="element-1078" />
                          Website
                        </a>}
                      {contributor.social.email && <a href={`mailto:${contributor.social.email}`} className="text-xs font-bold uppercase tracking-wider text-gray-500 hover:text-brand-orange transition-colors flex items-center gap-1" data-id="element-1079">
                          <Mail className="h-3 w-3" data-id="element-1080" />
                          Email
                        </a>}
                      {contributor.social.instagram && <a href={`https://instagram.com/${contributor.social.instagram}`} target="_blank" rel="noopener noreferrer" className="text-xs font-bold uppercase tracking-wider text-gray-500 hover:text-brand-orange transition-colors" data-id="element-1081">
                          Instagram
                        </a>}
                      {contributor.social.twitter && <a href={`https://twitter.com/${contributor.social.twitter}`} target="_blank" rel="noopener noreferrer" className="text-xs font-bold uppercase tracking-wider text-gray-500 hover:text-brand-orange transition-colors" data-id="element-1082">
                          X/Twitter
                        </a>}
                    </div>}
                </div>
              </div>
            </motion.div>)}
        </div>
      </section>

      {/* Call for Contributors */}
      <section className="py-20 bg-brand-navy text-white mt-16" data-id="element-1083">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1084">
          <h2 className="text-3xl font-black uppercase mb-6" data-id="element-1085">
            Want to Contribute?
          </h2>
          <p className="text-xl mb-8 opacity-90" data-id="element-1086">
            We're always looking for fat writers, poets, artists, and reporters.
            You don't need to be a "professional" — we care about your voice.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-1087">
            <Link to="/clusters/contribute" className="inline-flex items-center justify-center px-8 py-4 bg-brand-orange text-white font-bold uppercase tracking-wide hover:bg-brand-magenta transition-colors" data-id="element-1088">
              How to Contribute <ArrowRight className="ml-2 h-4 w-4" data-id="element-1089" />
            </Link>
            <Link to="/contact" className="inline-flex items-center justify-center px-8 py-4 border-2 border-white text-white font-bold uppercase tracking-wide hover:bg-white hover:text-brand-navy transition-colors" data-id="element-1090">
              Get in Touch
            </Link>
          </div>
        </div>
      </section>
    </div>;
}